import {
  defaultTypeCurrency,
  defaultTypeLocal,
  TaxTypes,
  TypeCurrency,
  TypeCurrentSubscription,
  TypeCycle,
  TypeLocal,
  TypeProduct,
} from '../types/general-types';
import {
  CurrencySettings,
  CurrentSubscriptionInfo,
  CycleDisplayProperties,
  CyclePricing,
  GetUserOfferingResponse,
  IntervalUnit,
  ProductInfo,
  SalePricing,
} from '@wix/ambassador-premium-store/types';
import {
  MAP_PRODUCT_ID_TO_SOCIAL_POSTS,
  PACKAGE_IDS,
  TECH_ERRORS,
  UNLIMITED_STRING,
} from '../constants';

const bundlePackadges = [
  '',
  'fc002183-deaa-48b5-994c-de613a095c14',
  '3c7e283f-dff4-4407-b678-00a28e4becb9',
  'b8772926-1d84-4694-9c4b-3fa583b45403',
];
const orderOfProductIds = [
  '',
  PACKAGE_IDS.BASIC_LEGACY,
  PACKAGE_IDS.PROFESSIONAL_LEGACY,
  PACKAGE_IDS.UNLIMITED_LEGACY,
];

const brazilTestProducts = [
  '',
  PACKAGE_IDS.BRAZIL_BASIC,
  PACKAGE_IDS.BRAZIL_PROFESSIONAL,
  PACKAGE_IDS.BRAZIL_UNLIMITED,
];

const newOrderOfProductIds = [
  '',
  PACKAGE_IDS.BASIC,
  PACKAGE_IDS.PROFESSIONAL,
  PACKAGE_IDS.UNLIMITED,
];

const orderOfCycles = [1, 12, 24];

export class ClientOffering {
  products: TypeProduct[] = [];
  currentProduct?: TypeCurrentSubscription;
  currency: TypeCurrency = defaultTypeCurrency;
  local: TypeLocal = defaultTypeLocal;
  experiments?: any;
  constructor(
    serverOffering?: GetUserOfferingResponse | any,
    clientOffering?: ClientOffering | any,
  ) {
    if (clientOffering) {
      const { currency, products, currentProduct, local } = clientOffering;
      this.currency = currency;
      this.products = products;
      this.currentProduct = currentProduct;
      this.local = local;
    } else if (serverOffering) {
      this.currency = this.transformCurrency(
        serverOffering.currencySettings,
        serverOffering.taxSettings?.displayTaxType,
        serverOffering.taxSettings?.showPricesWithTax,
      );
      this.currentProduct =
        serverOffering.currentSubscriptionInfo &&
        this.transformCurrentProduct(
          serverOffering.currentSubscriptionInfo,
          serverOffering.experiments,
        );
      this.products = this.transformProducts(
        serverOffering.products,
        this.currentProduct,
        serverOffering.experiments,
      );
      this.local = { code: 'en' };
    }
  }

  transformCycles = (
    cycles: CyclePricing[],
    numberOfPurchasableCycles: number,
    cycleDisplay: CycleDisplayProperties[],
    salesCycles?: SalePricing,
  ) => {
    const returnCycles: TypeCycle[] = [];
    orderOfCycles.forEach((numberOfMonths, index) => {
      const cycle = cycles?.find(
        (_cycle) => _cycle.monthsPerCycle === numberOfMonths,
      );
      if (cycle) {
        if (!cycle?.price || !cycle?.monthsPerCycle) {
          throw new Error(
            `${TECH_ERRORS.PRODUCT_NOT_FOUND} - ${numberOfMonths}`,
          );
        }
        const monthsPerCycle = cycle.monthsPerCycle;
        const price =
          Math.ceil(cycle.price / monthsPerCycle) /
          Math.pow(10, this.currency.minorUnits);
        const priceWithTax =
          Math.ceil((cycle.priceWithTax ?? 0) / monthsPerCycle) /
          Math.pow(10, this.currency.minorUnits);

        const isEnabled = 2 - index < numberOfPurchasableCycles;

        const discountPrices = salesCycles?.discountPrices;
        const foundSalesCycles = discountPrices
          ? discountPrices.find(
              (_discount: any) => _discount.monthsPerCycle === monthsPerCycle,
            )
          : undefined;
        const discountedPrice = foundSalesCycles
          ? Math.ceil((foundSalesCycles.price || 0) / monthsPerCycle) /
            Math.pow(10, this.currency.minorUnits)
          : undefined;

        const discountedPriceWithTax = foundSalesCycles
          ? Math.ceil((foundSalesCycles.priceWithTax || 0) / monthsPerCycle) /
            Math.pow(10, this.currency.minorUnits)
          : undefined;

        const unit = cycle.cycle?.interval?.unit || '';
        const count = cycle.cycle?.interval?.count || 1;
        const name = cycleDisplay[index]
          ? cycleDisplay[index].displayProperties?.name || ''
          : '';

        const cycleObj: TypeCycle = {
          monthsPerCycle,
          price,
          priceWithTax,
          isEnabled,
          discountedPrice,
          discountedPriceWithTax,
          unit,
          count,
          name,
        };
        returnCycles.push(cycleObj);
      } else {
        throw new Error(`${TECH_ERRORS.PRODUCT_NOT_FOUND} - ${numberOfMonths}`);
      }
    });

    returnCycles[1].savePercentage = Math.round(
      (1 - returnCycles[1].price / returnCycles[0].price) * 100,
    );
    returnCycles[2].savePercentage = Math.round(
      (1 - returnCycles[2].price / returnCycles[0].price) * 100,
    );
    returnCycles[1].saveTotalAmount =
      returnCycles[0].price - returnCycles[1].price;
    returnCycles[2].saveTotalAmount =
      returnCycles[0].price - returnCycles[2].price;
    return returnCycles;
  };

  transformProducts = (
    products: ProductInfo[],
    currentProduct?: TypeCurrentSubscription,
    experiments?: any,
  ) => {
    const returnProducts: TypeProduct[] = [];
    let index = 0;
    const packagesOrderId =
      experiments && experiments.showAscendMonetizationChanges
        ? newOrderOfProductIds
        : orderOfProductIds;
    packagesOrderId.forEach((orderProductId, _index) => {
      if (orderProductId && orderProductId !== '') {
        const product = products?.find(
          (_product) =>
            _product.productId === orderProductId ||
            _product.productId === brazilTestProducts[_index],
        );
        if (product) {
          const productId = product.productId ?? 'EmptyProductId';
          const name = product.name ?? 'EmptyName';
          const pricingInfo = product.pricingInfo;
          const cycleDisplay = product.cycleDisplayProperties;
          const catalogPricing = pricingInfo?.catalogPricing;
          const salesCycle = pricingInfo?.salePricing;
          const numberOfPurchasableCycles =
            product.purchasableCycles?.length || 0;
          const cycles: TypeCycle[] = this.transformCycles(
            catalogPricing || [],
            numberOfPurchasableCycles,
            cycleDisplay || [],
            salesCycle,
          );
          const price = cycles[1].price;
          const priceWithTax = cycles[1].priceWithTax;
          const isEnabled =
            numberOfPurchasableCycles > 0 &&
            !(
              currentProduct?.isBundled &&
              currentProduct?.productIndex >= _index - 1
            );
          const productLimitations = this.transformProductLimitations(product);

          const productObj: TypeProduct = {
            name,
            productId,
            price,
            priceWithTax,
            cycles,
            isEnabled,
            productLimitations,
          };
          returnProducts.push(productObj);
          index++;
        } else {
          throw new Error(
            `${TECH_ERRORS.PRODUCT_NOT_FOUND} - ${orderProductId}`,
          );
        }
      }
    });
    return returnProducts;
  };

  transformCurrency = (
    currencySettings?: CurrencySettings,
    inputTaxType?: TaxTypes,
    showPricesWithTax?: boolean,
  ) => {
    const minorUnits =
      currencySettings?.minorUnits !== undefined
        ? currencySettings.minorUnits
        : this.currency.minorUnits;
    const decimalSeparator =
      currencySettings?.decimalSeparator || this.currency.decimalSeparator;
    const groupSeparator =
      currencySettings?.groupSeparator || this.currency.groupSeparator;
    const negativePrefix =
      currencySettings?.negativePrefix || this.currency.negativePrefix;
    const negativeSuffix =
      currencySettings?.negativeSuffix || this.currency.negativeSuffix;
    const positivePrefix =
      currencySettings?.positivePrefix || this.currency.positivePrefix;
    const positiveSuffix =
      currencySettings?.positiveSuffix || this.currency.positiveSuffix;
    const symbol = currencySettings?.symbol || this.currency.symbol;
    const taxType = showPricesWithTax ? inputTaxType : undefined;

    return {
      minorUnits,
      decimalSeparator,
      groupSeparator,
      negativePrefix,
      negativeSuffix,
      positivePrefix,
      positiveSuffix,
      symbol,
      taxType,
    };
  };

  transformCurrentProduct = (
    currentSubscription: CurrentSubscriptionInfo,
    experiments?: any,
  ): TypeCurrentSubscription => {
    const productId = currentSubscription.productId || '';
    const isBundled = currentSubscription.isBundled || false;
    const indexOfProductId = isBundled
      ? bundlePackadges.indexOf(productId)
      : experiments && experiments.showAscendMonetizationChanges
      ? newOrderOfProductIds.indexOf(productId)
      : orderOfProductIds.indexOf(productId);
    const productIndex = indexOfProductId - 1;
    const cycleIndex =
      currentSubscription.cycle?.interval?.unit === IntervalUnit.MONTH
        ? 0
        : currentSubscription.cycle?.interval?.count === 1
        ? 1
        : 2;
    const subscriptionId = currentSubscription.subscriptionId || '';

    return {
      subscriptionId,
      productId,
      isBundled,
      productIndex,
      cycleIndex,
    };
  };

  transformProductLimitations = (product: ProductInfo) => {
    const features = product.features || [];
    let emailsCampaignsMonth: number | string = '';
    let emailsSendMonth: number | string = '';
    let formsMonth: number | string = '';
    let automations: number | string = '';
    let socialPosts: number | string = '';
    let videoCreation: number | string = '';
    let paymentRecurring: number | string = '';
    let siteContributors: number | string = '';
    const UNLIMITED = UNLIMITED_STRING;

    features.forEach((feature) => {
      switch (feature.name) {
        case 'Campaigns per Month':
          emailsCampaignsMonth =
            feature.quotaFeature?.limit || UNLIMITED_STRING;
          break;
        case 'Total Monthly Emails':
          emailsSendMonth = feature.quotaFeature?.limit || UNLIMITED_STRING;
          break;
        case 'Customizable Forms':
          formsMonth = feature.quotaFeature?.limit || UNLIMITED_STRING;
          break;
        case 'Automations':
          automations = feature.quotaFeature?.limit || UNLIMITED_STRING;
          break;
        case 'Video Maker Movie':
          videoCreation = feature.quotaFeature?.limit || UNLIMITED_STRING;
          break;
        case 'Recurring payments ':
          paymentRecurring = feature.quotaFeature?.limit || UNLIMITED_STRING;
          break;
        case 'Assign to Team Member':
          siteContributors = feature.quotaFeature?.limit || UNLIMITED_STRING;
          break;
        default:
          break;
      }
    });

    socialPosts =
      MAP_PRODUCT_ID_TO_SOCIAL_POSTS[product.productId || ''] || '0';

    return {
      emailsCampaignsMonth,
      emailsSendMonth,
      formsMonth,
      automations,
      socialPosts,
      videoCreation,
      paymentRecurring,
      siteContributors,
      UNLIMITED,
    };
  };

  getOffering = () => ({
    products: this.products,
    currentProduct: this.currentProduct,
    currency: this.currency,
    local: this.local,
  });

  private productList: any = null;

  getProductList = (freeProductName: string) => {
    if (this.productList) {
      return this.productList;
    }
    const freeProduct = {
      name: freeProductName,
      price: 0,
      productId: '',
      cycles: [],
      isEnabled: false,
    };
    this.productList = this.getProducts()
      .sort((a, b) => b.price - a.price)
      .map((_) => _);
    this.productList.push(freeProduct);
    return this.productList;
  };

  getProducts = () => this.products;

  getCurrentPlanName = (freeProductName: string) => {
    const productList = this.getProductList(freeProductName);
    const currentProductIndex = this.getCurrentProductIndex();
    return productList[currentProductIndex].name;
  };

  isCurrentProduct = {
    limited: () => !this.currentProduct,
    basic: () => this.currentProduct?.productIndex === 0,
    pro: () => this.currentProduct?.productIndex === 1,
    unlimited: () => this.currentProduct?.productIndex === 2,
  };

  getCurrentProductIndex = () => {
    return this.currentProduct ? 2 - this.currentProduct.productIndex : 3;
  };

  getCurrency = (): string => {
    return (
      new DOMParser().parseFromString(this.currency.symbol, 'text/html').body
        .textContent || '$'
    );
  };

  getCurrentCycleName() {
    if (this.currentProduct) {
      const { productIndex, cycleIndex } = this.currentProduct;
      return this.products[productIndex].cycles[cycleIndex].name;
    }
  }

  getProductNameById(id: string) {
    const product = this.products.find((el) => el.productId === id);
    return product?.name;
  }

  isSaleOn = () =>
    this.products[2].cycles[1].discountedPrice ||
    this.products[1].cycles[1].discountedPrice;

  getProductLimitations = (productId: number) => {
    const productIndex = 3 - productId;
    const products = this.getProducts();
    const product = products.find(
      (_prod) =>
        _prod.productId === orderOfProductIds[productIndex] ||
        _prod.productId === brazilTestProducts[productIndex] ||
        _prod.productId === newOrderOfProductIds[productIndex],
    );
    return product ? product.productLimitations : {};
  };
}
